// generated by the plop

import { Injectable } from '@angular/core';
import { Action, NgxsOnInit, Selector, State, StateContext } from '@ngxs/store';
import { cloneDeep } from 'lodash';
import { PROGRESS_STATUSES } from 'symbols';
import { ContractsService } from '../../services/contracts.service';
import { ContractModel, CONTRACTS_LOCAL_STORAGE_KEY } from '../../symbols';
import {
  CreateContract,
  CreateContractFail,
  CreateContractSuccess,
  CreateContractTemplate,
  CreatedContractByWS,
  DeleteContractTemplate,
  DeleteLocalContract,
  GetContractById,
  GetContractByIdFail,
  GetContractByIdSuccess,
  GetContractTemplateList,
  SetCurrentContractId,
  UpdateContract,
  UpdateContractFail,
  UpdateContractSuccess,
  UpdateContractTemplate,
  UpdateContractTemplateFail,
  UpdateContractTemplateSuccess,
  UpdatedContractByWS,
  UpdateLocalContract
} from './contract-manage.actions';
import { CONTRACT_MANAGE_STATE_DEFAULT, ContractManageStateModel } from './contract-manage.model';


@State({ name: 'ContractManage', defaults: CONTRACT_MANAGE_STATE_DEFAULT })
@Injectable()
export class ContractManageState implements NgxsOnInit {

  constructor(private contractsService: ContractsService) {
  }


  @Selector()
  static realVersionContracts(state: ContractManageStateModel): ContractManageStateModel['realVersionContracts'] {
    return state.realVersionContracts;
  }

  @Selector()
  static currentContract(state: ContractManageStateModel): ContractModel {
    return state.contracts?.[state.currentContractId];
  }

  @Selector()
  static loadContractStatus(state: ContractManageStateModel): PROGRESS_STATUSES {
    return state.loadContractStatus;
  }

  @Selector()
  static contractTemplateList(state: ContractManageStateModel): ContractManageStateModel['templateList'] {
    return state.templateList;
  }

  @Selector()
  static loadTemplateListStatus(state: ContractManageStateModel): PROGRESS_STATUSES {
    return state.loadTemplateListStatus;
  }

  ngxsOnInit(ctx: StateContext<ContractManageStateModel>) {
    const localContracts = localStorage.getItem(CONTRACTS_LOCAL_STORAGE_KEY);
    if (localContracts) {
      const contracts = JSON.parse(localContracts);
      ctx.patchState({
        contracts
      });
    }
    ctx.dispatch(new GetContractTemplateList());
  }

  @Action(GetContractTemplateList)
  getContractTemplateList(ctx: StateContext<ContractManageStateModel>): void {
    ctx.patchState({
      loadTemplateListStatus: PROGRESS_STATUSES.IN_PROGRESS
    });
    this.contractsService.getContractTemplateList().subscribe({
      next: templateList => ctx.patchState({ templateList, loadTemplateListStatus: PROGRESS_STATUSES.SUCCESS }),
      error: err => ctx.patchState({ loadTemplateListStatus: PROGRESS_STATUSES.FAIL })
    });
  }

  @Action(CreateContractTemplate)
  createContractTemplate(ctx: StateContext<ContractManageStateModel>, {
    template,
    name
  }: CreateContractTemplate): void {
    ctx.patchState({
      loadTemplateListStatus: PROGRESS_STATUSES.IN_PROGRESS
    });
    this.contractsService.createTemplate(name, template).subscribe({
      next: () => ctx.dispatch([new GetContractTemplateList(), new UpdateContractTemplateSuccess()]),
      error: err => {
        ctx.patchState({ loadTemplateListStatus: PROGRESS_STATUSES.FAIL });
        ctx.dispatch(new UpdateContractTemplateFail(err));
      }
    });
  }

  @Action(UpdateContractTemplate)
  updateContractTemplate(ctx: StateContext<ContractManageStateModel>, { template, id }: UpdateContractTemplate): void {
    ctx.patchState({
      loadTemplateListStatus: PROGRESS_STATUSES.IN_PROGRESS
    });
    this.contractsService.updateTemplate(id, {
      html: template.html,
      project: template.project,
      formatId: template.formatId,
      css: template.css
    }, template.name).subscribe({
      next: () => ctx.dispatch([new GetContractTemplateList(), new UpdateContractTemplateSuccess()]),
      error: err => {
        ctx.patchState({ loadTemplateListStatus: PROGRESS_STATUSES.FAIL });
        ctx.dispatch(new UpdateContractTemplateFail(err));
      }
    });
  }

  @Action(DeleteContractTemplate)
  deleteContractTemplate(ctx: StateContext<ContractManageStateModel>, { id }: DeleteContractTemplate): void {
    ctx.patchState({
      loadTemplateListStatus: PROGRESS_STATUSES.IN_PROGRESS
    });
    this.contractsService.deleteTemplate(id).subscribe({
      next: () => ctx.dispatch([new GetContractTemplateList(), new UpdateContractTemplateSuccess()]),
      error: err => {
        ctx.patchState({ loadTemplateListStatus: PROGRESS_STATUSES.FAIL });
        ctx.dispatch(new UpdateContractTemplateFail(err));
      }
    });
  }


  @Action(GetContractById)
  getContractById(ctx: StateContext<ContractManageStateModel>, { id }: GetContractById): void {
    ctx.patchState({
      loadContractStatus: PROGRESS_STATUSES.IN_PROGRESS
    });
    if (ctx.getState().contracts?.[id]?.id) {
      this.contractsService.getContractById(id).subscribe({
        next: contract => {
          if (contract) {
            ctx.dispatch(new UpdatedContractByWS(contract));
          } else {
            ctx.patchState({
              loadContractStatus: PROGRESS_STATUSES.SUCCESS
            });
          }
          ctx.patchState({
            currentContractId: id
          });
        },
        error: err => ctx.dispatch(new GetContractByIdFail(err))
      });
    } else {
      this.contractsService.getContractById(id).subscribe({
        next: contract => ctx.dispatch(new GetContractByIdSuccess(contract)),
        error: err => ctx.dispatch(new GetContractByIdFail(err))
      });
    }
  }

  @Action(GetContractByIdSuccess)
  getContractByIdSuccess(ctx: StateContext<ContractManageStateModel>, { contract }: GetContractByIdSuccess): void {
    const contracts = cloneDeep(ctx.getState().contracts);
    contracts[contract.id] = contract;
    ctx.patchState({
      contracts,
      currentContractId: contract.id,
      loadContractStatus: PROGRESS_STATUSES.SUCCESS
    });
  }

  @Action(GetContractByIdFail)
  getContractByIdFail(ctx: StateContext<ContractManageStateModel>, { error }: GetContractByIdFail): void {
    ctx.patchState({
      loadContractStatus: PROGRESS_STATUSES.FAIL
    });
  }

  @Action(SetCurrentContractId)
  setCurrentContractId(ctx: StateContext<ContractManageStateModel>, { id }: SetCurrentContractId): void {
    ctx.patchState({
      currentContractId: id
    });
  }

  @Action(UpdateLocalContract)
  updateLocalContract(ctx: StateContext<ContractManageStateModel>, { contract }: UpdateLocalContract): void {
    const contracts = cloneDeep(ctx.getState().contracts);
    contracts[contract.id] = { ...contracts[contract.id], ...contract };
    localStorage.setItem(CONTRACTS_LOCAL_STORAGE_KEY, JSON.stringify(contracts));
    ctx.patchState({
      contracts
    });
  }

  @Action(DeleteLocalContract)
  deleteLocalContract(ctx: StateContext<ContractManageStateModel>, { id }: DeleteLocalContract): void {
    const contracts = cloneDeep(ctx.getState().contracts);
    const realVersionContracts = cloneDeep(ctx.getState().realVersionContracts);

    delete contracts[id];
    delete realVersionContracts[id];
    ctx.patchState({
      contracts,
      realVersionContracts
    });
  }


  @Action(UpdateContract)
  updateContract(ctx: StateContext<ContractManageStateModel>, { id, contract, force }: UpdateContract): void {
    ctx.patchState({
      updateContractStatus: PROGRESS_STATUSES.IN_PROGRESS
    });

    this.contractsService.updateContract(id, contract, force).subscribe({
      next: updatedContract => ctx.dispatch(new UpdateContractSuccess(updatedContract)),
      error: err => ctx.dispatch(new UpdateContractFail(err))
    });
  }

  @Action(UpdateContractSuccess)
  updateContractSuccess(ctx: StateContext<ContractManageStateModel>, { contract }: UpdateContractSuccess): void {
    const contracts = cloneDeep(ctx.getState().contracts);
    ctx.patchState({
      updateContractStatus: PROGRESS_STATUSES.SUCCESS,
      contracts: { ...contracts, [contract.id]: { ...contracts[contract.id], version: contract.version, debtId: contract.debtId, debt: contract.debt } }
    });
  }

  @Action([UpdatedContractByWS])
  updateContractByWS(ctx: StateContext<ContractManageStateModel>, { contract }: UpdatedContractByWS): void {
    const realVersionContracts = cloneDeep(ctx.getState().realVersionContracts);
    const contracts = cloneDeep(ctx.getState().contracts);
    const contractExist = contracts[contract.id];

    if (contractExist) {
      ctx.patchState({
        loadContractStatus: PROGRESS_STATUSES.SUCCESS,
        contracts: {
          ...contracts,
          [contract.id]: { ...contracts[contract.id], updater: contract.updater }
        }
      });
    }
    ctx.patchState({
      loadContractStatus: PROGRESS_STATUSES.SUCCESS,
      realVersionContracts: { ...realVersionContracts, [contract.id]: contract }
    });
  }

  @Action(CreatedContractByWS)
  createdContractByWS(ctx: StateContext<ContractManageStateModel>, { contract }: CreatedContractByWS): void {
    const contracts = cloneDeep(ctx.getState().contracts);
    ctx.patchState({
      contracts: {
        ...contracts,
        [contract.id]: {
          ...contracts[contract.id],
          isChanged: false,
          isNew: false,
          updater: contract.updater
        }
      }
    });
  }


  @Action(CreateContract)
  createContract(ctx: StateContext<ContractManageStateModel>, { contract }: CreateContract): void {
    ctx.patchState({
      updateContractStatus: PROGRESS_STATUSES.IN_PROGRESS
    });

    this.contractsService.createContract(contract).subscribe({
      next: createdContract => ctx.dispatch(new CreateContractSuccess(createdContract)),
      error: err => ctx.dispatch(new CreateContractFail(err))
    });
  }

  @Action(CreateContractSuccess)
  createContractSuccess(ctx: StateContext<ContractManageStateModel>, { contract }: CreateContractSuccess): void {
    const contracts = cloneDeep(ctx.getState().contracts);
    ctx.patchState({
      updateContractStatus: PROGRESS_STATUSES.SUCCESS,
      contracts: { ...contracts, [contract.id]: { ...contracts[contract.id], debtId: contract.debtId, debt: contract.debt } }
    });
  }

  @Action(CreateContractFail)
  createContractFail(ctx: StateContext<ContractManageStateModel>, { error }: CreateContractFail): void {
    ctx.patchState({
      updateContractStatus: PROGRESS_STATUSES.FAIL
    });
  }
}


